import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes } from 'react-router'
import './style.css'
import Landing from './pages/landing/Landing.tsx'
import Pricing from './pages/pricing/Pricing.tsx'
import About from './pages/about/About.tsx'
import Support from './pages/support/Support.tsx'
import Login from './pages/login/Login.tsx'
import Clubs from './pages/clubs/Clubs.tsx'
import AppLayout from './layout/AppLayout.tsx'
import ClubMeetings from './pages/club/meetings/ClubMeetings.tsx'
import ClubAdminPanelPage from './pages/club/admin/ClubAdminPanelPage.tsx'
import { initializeApp } from 'firebase/app'
import * as firebaseConfig from '../firebase.json'
import Profile from './pages/profile/Profile.tsx'
import MeetingDetails from './pages/club/meeting/MeetingDetails.tsx'
import { Provider } from 'react-redux'
import { store } from './app/ReduxStore.ts'
import { connectAuthEmulator, getAuth, onAuthStateChanged } from 'firebase/auth'
import { FirebaseAppUser } from './types/Types.ts'
import { setFirebaseUser } from './features/FirebaseAuthSlice.ts'
import { StrictMode } from 'react'
import initSentry from './integrations/Sentry.ts'
import { initClarity, identifyClarityUser } from './integrations/Clarity.ts'
import Error404 from './pages/Error404.tsx'
import NetworkErrorPage from './pages/NetworkErrorPage.tsx'
import GenericErrorPage from './pages/GenericErrorPage.tsx'
import ErrorUserNotFound from './pages/ErrorUserNotFound.tsx'
import ErrorClubNotFound from './pages/ErrorClubNotFound.tsx'
import SignUpWithLink from './components/login/SignUpWithLink.tsx'
import SignInWithLink from './components/login/SignInWithLink.tsx'
import RegisterClub from './pages/register/RegisterClub.tsx'
import SignUp from './pages/register/SignUp.tsx'
import CompleteSignUp from './pages/register/CompleteSignUp.tsx'
import { speechAgendaApi } from './features/SpeechAgendaApiSlice.ts'
import AcceptInvite from './components/invite/AcceptInvite.tsx'
import Unsubscribe from './pages/unsubscribe/Unsubscribe.tsx'
import ScrollToTop from './components/ScrollToTop.tsx'

initSentry()
initClarity()

const firebaseApp = initializeApp(firebaseConfig)
export { firebaseApp }

const auth = getAuth()

// Set only by the e2e image build. A production build leaves it out and talks to Firebase
const authEmulatorUrl = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL
if (authEmulatorUrl) {
  connectAuthEmulator(auth, authEmulatorUrl, { disableWarnings: true })
}

onAuthStateChanged(auth, async (user) => {
  let firebaseAppUser: FirebaseAppUser | null
  if (user) {
    firebaseAppUser = {
      idpId: user.uid,
      accessToken: await user.getIdToken(true),
    }
    identifyClarityUser(user.uid)
  } else {
    firebaseAppUser = null
  }
  store.dispatch(speechAgendaApi.util.resetApiState())
  store.dispatch(setFirebaseUser(firebaseAppUser))
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/support" element={<Support />} />
          <Route element={<AppLayout />}>
            <Route path="/clubs" element={<Clubs />} />
            <Route path="/club/:clubId" element={<ClubMeetings />} />
            <Route
              path="/club/:clubId/admin"
              element={<ClubAdminPanelPage />}
            />
            <Route path="/profile" element={<Profile />} />
            <Route
              path="/club/:clubId/meeting/:meetingTimestampUtc"
              element={<MeetingDetails />}
            />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/register-club" element={<RegisterClub />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/complete-sign-up" element={<CompleteSignUp />} />
          {/*Errors */}
          <Route path="/error" element={<GenericErrorPage />} />
          <Route path="/network-error" element={<NetworkErrorPage />} />
          <Route path="/not-found" element={<Error404 />} />
          <Route path="/user-not-found" element={<ErrorUserNotFound />} />
          <Route path="/club-not-found" element={<ErrorClubNotFound />} />
          <Route path="/sign-up-with-link" element={<SignUpWithLink />} />
          <Route path="/sign-in-with-link" element={<SignInWithLink />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
          <Route path="*" element={<Error404 />} />
        </Routes>
      </BrowserRouter>
    </Provider>
  </StrictMode>
)
